import React, { useState } from 'react';

import { ellipsis } from 'polished';
import styled from 'styled-components';
import dynamic from 'next/dynamic';
import Image from 'next/image';

import { Separator } from '@components/formStyles';
import { useDevice } from '@contexts/device';
import CustomIcon from '@components/utils/CustomIcon';
import palette from '@utils/theme/palette';
import Typography from '@components/Typography';
import {
  ReviewTripTypeViewModel,
  ReviewViewModel,
  SubratingViewModel,
} from '@api/v3';
import { ExpediaReviewIcon } from '@assets/icons';

import {
  CommentContainer,
  CommentIconContainer,
  CommentsWrapper,
  Container,
  Examination,
  NoteWrapper,
  ShowMoreButton,
} from './scoringStyle';
import { getExpediaReviewIcon, getExpediaReviewType } from './getReviewIcon';
import { cloudineryImage } from '../../utils/features';
const DesktopHotelDetailScoring = dynamic(
  () => import('./DesktopHotelDetailScoring')
);
const MobileHotelDetailScoring = dynamic(
  () => import('./MobileHotelDetailScoring')
);
interface ReadMoreSectionProps {
  text: string;
  onClick?: () => void;
}
export const ReadMore = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.palette.coral};
  cursor: pointer;
  width: 100%;
`;
const DescriptionText = styled.div<{ showMore?: boolean }>`
  font-size: 14px;
  margin-bottom: 5px;
  ${({ showMore }) => showMore && ellipsis(undefined, 3)}
`;
const Content = ({ info }: { info?: ReviewViewModel; idx: number }) => {
  const { isDesktop } = useDevice();
  const LIMIT = 520;
  const [isReadMore, setisReadMore] = useState<boolean>();
  const ReadMoreSection = ({ text, onClick }: ReadMoreSectionProps) => (
    <ReadMore onClick={onClick}>{text}</ReadMore>
  );
  const onReadMore = () => {
    setisReadMore(!isReadMore);
  };
  return (
    <>
      <Typography variant={isDesktop ? 'caption' : 'Title5'} weight='SemiBold'>
        {info?.title}
      </Typography>
      <DescriptionText>
        {isReadMore
          ? info?.review
          : info?.review
              ?.substring(0, LIMIT)
              ?.concat(info?.review?.length > LIMIT ? '...' : '')}
      </DescriptionText>
      {(info?.review?.length ?? 0) > LIMIT ? (
        <ReadMoreSection
          text={!isReadMore ? 'Devamını Oku' : 'Daha az göster'}
          onClick={onReadMore}
        />
      ) : null}
    </>
  );
};
export type ScoringProps = {
  totalScoring?: number;
  subratings?: SubratingViewModel[];
  reviewCount?: number;
  reviews?: Array<ReviewViewModel>;
  tripTypes?: ReviewTripTypeViewModel[];
};

const HotelDetailScoring = ({
  subratings,
  reviews,
  totalScoring,
  tripTypes,
  reviewCount,
}: ScoringProps) => {
  const { isDesktop } = useDevice();
  const [displayedCount, setDisplayedCount] = useState(10);
  const MAX_DISPLAY = 10;

  const showMoreComment = () => {
    if (reviews && displayedCount + MAX_DISPLAY > reviews?.length) {
      setDisplayedCount(reviews?.length);
    } else {
      setDisplayedCount(displayedCount + MAX_DISPLAY);
    }
  };

  return (
    <Container>
      <CommentContainer>
        <Typography variant={isDesktop ? 'Title4' : 'Title2'} weight='Bold'>
          Puan ve Yorumlar
        </Typography>
        <Examination>
          <Typography variant='caption1' weight='SemiBold'>
            {reviewCount} inceleme
          </Typography>
        </Examination>
      </CommentContainer>
      <CustomIcon icon={ExpediaReviewIcon} width={100} height={20} />
      <NoteWrapper>
        <Typography
          variant='caption1'
          color='D500'
          style={{ lineHeight: '24px' }}
        >
          Otel puanı ve en güncel 100 yorum hiçbir müdahale olmaksızın
          Expedia’dan alınmaktadır.
        </Typography>
      </NoteWrapper>
      <Separator size={isDesktop ? '20px' : '10px'} />
      {isDesktop ? (
        <DesktopHotelDetailScoring
          subratings={subratings}
          totalScoring={totalScoring}
          tripTypes={tripTypes}
        />
      ) : (
        <MobileHotelDetailScoring
          subratings={subratings}
          totalScoring={totalScoring}
          tripTypes={tripTypes}
        />
      )}

      {reviews?.slice(0, displayedCount).map((item, index) => (
        <CommentsWrapper key={index + 1}>
          {item?.verificationSourceImage ? (
            <Image
              src={cloudineryImage(item?.verificationSourceImage, 96, 16)}
              alt={item?.verificationSource ?? ''}
              width={96}
              height={16}
            />
          ) : null}
          <CommentContainer>
            <Typography
              variant={isDesktop ? 'caption' : 'Title5'}
              weight='Bold'
              as='div'
            >
              {item?.reviewBy}
            </Typography>
            <Typography
              variant={isDesktop ? 'caption' : 'Title5'}
              weight='Bold'
            >
              {item?.date}
            </Typography>
          </CommentContainer>
          <CommentContainer>
            <Typography
              variant={isDesktop ? 'caption1' : 'caption2'}
              weight='Regular'
              style={{ lineHeight: '24px' }}
            >
              Genel değerlendirme :<strong>{item?.rating?.value}</strong>
            </Typography>
            {isDesktop ? (
              <CommentIconContainer>
                <CustomIcon
                  icon={getExpediaReviewIcon(item?.tripType ?? '')}
                  fontSize={18}
                  fill={palette.black}
                />
                <Typography
                  variant={isDesktop ? 'caption1' : 'caption2'}
                  weight='Regular'
                  style={{ lineHeight: '24px' }}
                >
                  {getExpediaReviewType(item?.tripType ?? '')}
                </Typography>
              </CommentIconContainer>
            ) : (
              <Typography
                variant={isDesktop ? 'caption1' : 'caption2'}
                weight='Regular'
                style={{ lineHeight: '24px' }}
              >
                {getExpediaReviewType(item?.tripType ?? '')}
              </Typography>
            )}
          </CommentContainer>
          <Separator size='8px' />
          <Content idx={index} info={item} />
        </CommentsWrapper>
      ))}
      {reviews &&
      reviews?.length > MAX_DISPLAY &&
      displayedCount < reviews.length ? (
        <ShowMoreButton onClick={() => showMoreComment()}>
          <Typography
            variant={isDesktop ? 'caption' : 'caption2'}
            weight='Bold'
            color='D400'
          >
            Daha Fazla Yorum Göster
          </Typography>
        </ShowMoreButton>
      ) : null}
    </Container>
  );
};
export default HotelDetailScoring;
